/**
 * FdsStudioPanel — dock de droite donnant accès à FDS Studio
 * (https://fds.polyprint-studio.fr), au même titre que le Studio I.A.
 *
 * ⚠️ L'app FDS refuse l'intégration en iframe (en-têtes `X-Frame-Options: DENY`
 * et CSP `frame-ancestors 'none'`). On ne PEUT donc pas l'afficher en ligne
 * dans la colonne : on propose un portail de lancement (fenêtre dédiée /
 * nouvel onglet). Le jour où l'app FDS autorisera labelforge.polyprint-studio.fr
 * dans `frame-ancestors`, passer FDS_CAN_EMBED à true suffit à basculer en
 * iframe — le reste de l'UI ne change pas.
 */
import React from 'react';

const FDS_URL = import.meta.env.VITE_FDS_URL || 'https://fds.polyprint-studio.fr/';
const FDS_CAN_EMBED = false; // ← true quand l'app FDS aura relâché frame-ancestors

const FdsIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="13" y2="17" />
    </svg>
);

/** Ouvre FDS dans une fenêtre dédiée (façon « modale » flottante) centrée. */
function openFdsWindow() {
    const w = Math.min(1280, Math.floor(window.screen.availWidth * 0.85));
    const h = Math.min(860, Math.floor(window.screen.availHeight * 0.9));
    const left = Math.max(0, Math.floor((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.floor((window.screen.availHeight - h) / 2));
    window.open(FDS_URL, 'fds-studio',
        `popup=yes,noopener,noreferrer,width=${w},height=${h},left=${left},top=${top}`);
}

export default function FdsStudioPanel() {
    const [panelWidth, setPanelWidth] = React.useState(460);
    const isResizing = React.useRef(false);

    React.useEffect(() => {
        const move = (e) => {
            if (isResizing.current) {
                setPanelWidth(Math.min(Math.max(340, document.body.clientWidth - e.clientX), 900));
            }
        };
        const up = () => {
            if (isResizing.current) { isResizing.current = false; document.body.style.cursor = 'default'; }
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    }, []);

    return (
        <div
            className="bg-black/40 backdrop-blur-xl border-l border-white/5 flex flex-col shrink-0 relative shadow-2xl overflow-hidden"
            style={{ width: panelWidth }}
        >
            {/* Poignée de redimensionnement */}
            <div
                className="absolute left-0 top-0 bottom-0 w-[6px] cursor-col-resize z-50 hover:bg-emerald-400/50 transition-colors"
                style={{ transform: 'translateX(-50%)' }}
                onMouseDown={(e) => { e.preventDefault(); isResizing.current = true; document.body.style.cursor = 'col-resize'; }}
            />

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-black/20 shrink-0">
                <div className="flex items-center gap-2 text-emerald-400">
                    <FdsIcon size={16} />
                    <span className="text-[11px] font-mono font-bold uppercase tracking-[0.2em]">FDS Studio</span>
                </div>
                <button
                    onClick={() => window.open(FDS_URL, '_blank', 'noopener,noreferrer')}
                    title="Ouvrir dans un nouvel onglet"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[9px] font-mono uppercase tracking-wider border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:text-white transition-all"
                >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                    Onglet
                </button>
            </div>

            {/* Corps */}
            {FDS_CAN_EMBED ? (
                <iframe
                    title="FDS Studio"
                    src={FDS_URL}
                    className="flex-1 w-full border-0 bg-white"
                />
            ) : (
                <div className="flex-1 flex flex-col items-center justify-center gap-5 px-7 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-[0_0_30px_rgba(16,185,129,0.2)]">
                        <FdsIcon size={30} />
                    </div>
                    <div>
                        <h3 className="text-white text-sm font-mono font-bold uppercase tracking-widest">FDS Studio</h3>
                        <p className="text-white/45 text-[11px] font-sans mt-1.5 leading-relaxed">
                            Fiches de données de sécurité &amp; données réglementaires.
                        </p>
                        <p className="text-emerald-400/70 text-[9px] font-mono mt-1 tracking-wider break-all">
                            fds.polyprint-studio.fr
                        </p>
                    </div>

                    <button
                        onClick={openFdsWindow}
                        className="w-full max-w-[240px] py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-mono font-bold uppercase tracking-widest text-[11px] transition-all shadow-[0_0_18px_rgba(16,185,129,0.35)] hover:shadow-[0_0_28px_rgba(16,185,129,0.55)] hover:scale-[1.02] flex items-center justify-center gap-2"
                    >
                        <FdsIcon size={15} />
                        Ouvrir FDS Studio
                    </button>

                    <p className="text-white/30 text-[9px] font-mono leading-relaxed max-w-[260px]">
                        FDS Studio s'ouvre dans une fenêtre dédiée : l'application protège
                        ses données par une politique de sécurité qui empêche son affichage
                        intégré dans une autre page.
                    </p>
                </div>
            )}
        </div>
    );
}
